import { buildCachedResultPayload, normalizeClarificationPayload } from './InsightsService';
import {
  buildRecentQuestionsScopeKey,
  upsertRecentQuestion,
} from './constants/insightsClient.js';

const state = {
  sessionId: null,
  lastRunId: null,
  lastResult: null,
  lastQuestion: null,
  clarification: null,
  conversationTurns: [],
  recentQuestionsByScope: {},
};

const mutations = {
  SET_SESSION_ID(state, sessionId) {
    state.sessionId = sessionId || null;
  },
  SET_LAST_RUN_ID(state, runId) {
    state.lastRunId = runId || null;
  },
  SET_LAST_RESULT(state, result) {
    state.lastResult = result || null;
  },
  SET_LAST_QUESTION(state, question) {
    state.lastQuestion = question || null;
  },
  SET_CLARIFICATION(state, payload) {
    if (!payload) {
      state.clarification = null;
      return;
    }
    const normalized = normalizeClarificationPayload(payload);
    state.clarification = {
      runId: normalized.runId,
      sessionId: normalized.sessionId,
      missingScope: normalized.missingScope,
      mode: normalized.clarification.mode,
      prompt: normalized.clarification.prompt,
      options: normalized.clarification.options,
      scope: normalized.scope,
      whyClarification: payload.whyClarification || normalized.reasoning?.whyClarification || null,
    };
    if (normalized.sessionId) {
      state.sessionId = normalized.sessionId;
    }
    if (normalized.runId) {
      state.lastRunId = normalized.runId;
    }
  },
  CLEAR_CLARIFICATION(state) {
    state.clarification = null;
  },
  RESET_SESSION(state) {
    state.sessionId = null;
    state.lastRunId = null;
    state.lastResult = null;
    state.lastQuestion = null;
    state.clarification = null;
    state.conversationTurns = [];
  },
  APPEND_TURN(state, turn) {
    if (!turn) return;
    state.conversationTurns = [...state.conversationTurns, turn];
  },
  UPDATE_TURN(state, { id, patch }) {
    if (!id || !patch) return;
    state.conversationTurns = state.conversationTurns.map((turn) => (
      turn.id === id ? { ...turn, ...patch } : turn
    ));
  },
  REMOVE_LAST_ACTIVITY_TURN(state) {
    const turns = state.conversationTurns;
    for (let i = turns.length - 1; i >= 0; i -= 1) {
      if (turns[i].type === 'assistant_activity') {
        state.conversationTurns = [...turns.slice(0, i), ...turns.slice(i + 1)];
        return;
      }
    }
  },
  DISMISS_LAST_CLARIFICATION_TURN(state) {
    const turns = state.conversationTurns;
    for (let i = turns.length - 1; i >= 0; i -= 1) {
      if (turns[i].type === 'assistant_clarification') {
        state.conversationTurns = turns.map((turn, idx) => (
          idx === i ? { ...turn, dismissed: true } : turn
        ));
        return;
      }
    }
  },
  UPDATE_PRESENTATION(state, presentation) {
    if (!state.lastResult || !presentation) return;
    const prior = state.lastResult.presentation || state.lastResult.presentationMeta || {};
    state.lastResult = {
      ...state.lastResult,
      presentation: { ...prior, ...presentation },
      presentationMeta: undefined,
    };
  },
  HYDRATE(state, payload) {
    for (const key of Object.keys(payload || {})) {
      if (key in state) {
        state[key] = payload[key];
      }
    }
  },
  RECORD_RECENT_QUESTION(state, { scopeKey, entry }) {
    const key = String(scopeKey || '').trim();
    if (!key || !entry?.question) return;
    const prior = Array.isArray(state.recentQuestionsByScope[key])
      ? state.recentQuestionsByScope[key]
      : [];
    state.recentQuestionsByScope = {
      ...state.recentQuestionsByScope,
      [key]: upsertRecentQuestion(prior, entry),
    };
  },
};

const actions = {
  setSessionFromResult({ commit }, result) {
    if (result?.sessionId) {
      commit('SET_SESSION_ID', result.sessionId);
    }
    if (result?.runId) {
      commit('SET_LAST_RUN_ID', result.runId);
    }
    if (result?.success && result.rows?.length > 0) {
      commit('SET_LAST_RESULT', buildCachedResultPayload(result));
    }
  },
  setClarification({ commit }, payload) {
    commit('SET_CLARIFICATION', payload);
  },
  clearClarification({ commit }) {
    commit('CLEAR_CLARIFICATION');
  },
  setLastQuestion({ commit }, question) {
    commit('SET_LAST_QUESTION', question);
  },
  dismissLastClarificationTurn({ commit }) {
    commit('DISMISS_LAST_CLARIFICATION_TURN');
  },
  resetSession({ commit }) {
    commit('RESET_SESSION');
  },
  applyPresentation({ commit }, { rows, question, presentation }) {
    const result = rePresentRows(rows, question, presentation);
    if (result.presentation) {
      commit('UPDATE_PRESENTATION', result.presentation);
    }
    return result;
  },
  appendTurn({ commit }, turn) {
    commit('APPEND_TURN', turn);
  },
  updateTurn({ commit }, payload) {
    commit('UPDATE_TURN', payload);
  },
  removeLastActivityTurn({ commit }) {
    commit('REMOVE_LAST_ACTIVITY_TURN');
  },
  recordRecentQuestion({ commit, rootState }, { question }) {
    const scopeKey = buildRecentQuestionsScopeKey(
      rootState.auth?.userId,
      rootState.auth?.institutionId,
    );
    if (!scopeKey) return;
    const trimmed = String(question || '').trim();
    if (!trimmed) return;
    commit('RECORD_RECENT_QUESTION', {
      scopeKey,
      entry: {
        question: trimmed,
        askedAt: Date.now(),
      },
    });
  },
};

/**
 * Re-sort cached rows for RE_PRESENT; honors server presentation.sort when present.
 * @param {Array} rows
 * @param {string} question
 * @param {object|null} presentation
 */
function rePresentRows(rows, question, presentation = null) {
  const sorted = [...rows];
  const columnKeys = (presentation?.columns || []).map((col) => col.key).filter(Boolean);
  const fallbackKeys = [
    ...(presentation?.primaryFields || []),
    ...(presentation?.secondaryFields || []),
  ];
  const keysForSort = columnKeys.length ? columnKeys : fallbackKeys;

  let sortColumn = presentation?.sort?.key || presentation?.sortColumn || null;
  let sortDirection = presentation?.sort?.direction || presentation?.sortDirection || 'asc';

  if (!sortColumn) {
    const q = String(question || '').toLowerCase();
    for (const col of keysForSort) {
      const colLower = col.toLowerCase();
      const spaced = col.replace(/([A-Z])/g, ' $1').toLowerCase().trim();
      if (q.includes(colLower) || q.includes(spaced)) {
        sortColumn = col;
        break;
      }
    }
    if (!sortColumn && /\bname\b/.test(q)) {
      sortColumn = keysForSort.find((c) => /first|last|name/i.test(c)) || null;
    }
    if (!sortColumn && /\b(performance|score)\b/.test(q)) {
      sortColumn = keysForSort.find((c) => /performance|score/i.test(c)) || null;
    }
    if (!sortColumn && /\bdate\b/.test(q)) {
      sortColumn = keysForSort.find((c) => /date|at|time/i.test(c)) || null;
    }
    if (/\b(desc|descending|highest|top|newest)\b/.test(q)) {
      sortDirection = 'desc';
    }
    if (/\b(asc|ascending|lowest|oldest)\b/.test(q)) {
      sortDirection = 'asc';
    }
  }

  if (sortColumn) {
    sorted.sort((a, b) => {
      const av = a[sortColumn];
      const bv = b[sortColumn];
      let cmp = 0;
      if (typeof av === 'number' && typeof bv === 'number') {
        cmp = av - bv;
      } else {
        cmp = String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true });
      }
      return sortDirection === 'desc' ? -cmp : cmp;
    });
  }

  const nextPresentation = presentation && typeof presentation === 'object'
    ? { ...presentation }
    : {};

  if (sortColumn) {
    nextPresentation.sort = { key: sortColumn, direction: sortDirection };
  }

  return {
    rows: sorted,
    presentation: nextPresentation,
  };
}

const getters = {
  hasActiveSession: (state) => !!state.sessionId,
  cachedResult: (state) => state.lastResult,
  clarification: (state) => state.clarification,
  conversationTurns: (state) => state.conversationTurns,
  recentQuestionsForScope: (state, _getters, rootState) => {
    const scopeKey = buildRecentQuestionsScopeKey(
      rootState.auth?.userId,
      rootState.auth?.institutionId,
    );
    if (!scopeKey) return [];
    const list = state.recentQuestionsByScope[scopeKey];
    return Array.isArray(list) ? list : [];
  },
};

export default {
  namespaced: true,
  persist: ['recentQuestionsByScope'],
  state,
  mutations,
  actions,
  getters,
};
